import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { TrendingUp, Users, DollarSign, Activity, Target, Percent, ArrowLeft } from 'lucide-react';

const BusinessMetricsPage = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      const response = await api.get('/metrics/business-metrics');
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to load metrics', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading business metrics...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return <div className="text-center py-12 text-gray-500">No metrics available</div>;
  }

  return (
    <div className="space-y-8" data-testid="business-metrics-page">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Business Metrics</h1>
          <p className="text-gray-600 mt-2">Key performance indicators for your SaaS business</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card data-testid="metric-mrr">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Recurring Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              €{metrics.overview.mrr.toLocaleString()}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              ARR: €{metrics.overview.arr.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="metric-users">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {metrics.users.total}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {metrics.users.active_paying} paying customers
            </p>
          </CardContent>
        </Card>

        <Card data-testid="metric-growth">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Growth Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {metrics.overview.growth_rate_monthly.toFixed(1)}%
            </div>
            <p className="text-xs text-gray-500 mt-1">
              +{metrics.users.new_7d} users this week
            </p>
          </CardContent>
        </Card>

        <Card data-testid="metric-conversion">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <Percent className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {metrics.overview.conversion_rate.toFixed(1)}%
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Churn: {metrics.overview.churn_rate}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Valuation */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-2xl">Estimated Valuation</CardTitle>
          <CardDescription>Based on current MRR and industry multiples</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Conservative (30x MRR)</p>
              <p className="text-3xl font-bold text-gray-900">
                €{metrics.valuation.conservative_30x.toLocaleString()}
              </p>
            </div>
            <div className="text-center p-4 bg-blue-100 rounded-lg border-2 border-blue-500">
              <Badge className="mb-2 bg-blue-600">Most Likely</Badge>
              <p className="text-sm text-gray-600 mb-2">Realistic (35x MRR)</p>
              <p className="text-3xl font-bold text-blue-600">
                €{metrics.valuation.realistic_35x.toLocaleString()}
              </p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Optimistic (40x MRR)</p>
              <p className="text-3xl font-bold text-gray-900">
                €{metrics.valuation.optimistic_40x.toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">MRR</span>
              <span className="font-bold text-lg">€{metrics.revenue.mrr}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ARR</span>
              <span className="font-bold text-lg">€{metrics.revenue.arr}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">MRR Growth (30d)</span>
              <span className="font-bold text-lg text-green-600">+€{metrics.revenue.mrr_growth_30d}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ARPU</span>
              <span className="font-bold text-lg">€{metrics.revenue.avg_revenue_per_user.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Customer LTV</span>
              <span className="font-bold text-lg">€{metrics.overview.ltv.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Users</span>
              <span className="font-bold text-lg">{metrics.users.total}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">New Users (30d)</span>
              <span className="font-bold text-lg text-blue-600">+{metrics.users.new_30d}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">New Users (7d)</span>
              <span className="font-bold text-lg text-blue-600">+{metrics.users.new_7d}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active Paying</span>
              <span className="font-bold text-lg text-green-600">{metrics.users.active_paying}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Free Users</span>
              <span className="font-bold text-lg">{metrics.users.free_users}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Platform Usage</CardTitle>
          <CardDescription>Product engagement metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Total Invoices Created</p>
                  <p className="text-2xl font-bold text-blue-600">{metrics.usage.total_invoices}</p>
                </div>
                <Activity className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-sm text-gray-500">
                {metrics.usage.invoices_30d} invoices in last 30 days
              </p>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Total Content Moderated</p>
                  <p className="text-2xl font-bold text-indigo-600">{metrics.usage.total_moderations}</p>
                </div>
                <Activity className="h-8 w-8 text-indigo-600" />
              </div>
              <p className="text-sm text-gray-500">
                {metrics.usage.moderations_30d} moderations in last 30 days
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-green-600" />
            <span>Key Selling Points for Buyers</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start space-x-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">
                <strong>Dual Revenue Streams:</strong> SmartFacture AI + LocalMod AI = diversified income
              </span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">
                <strong>Low Churn Rate:</strong> {metrics.overview.churn_rate}% monthly churn (target: &lt;3%)
              </span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">
                <strong>High Growth Potential:</strong> {metrics.overview.growth_rate_monthly.toFixed(1)}% monthly growth
              </span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">
                <strong>Modern Tech Stack:</strong> React 19, FastAPI, MongoDB, GPT-5.2, Claude Sonnet 4.5
              </span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">
                <strong>Scalable Architecture:</strong> Cloud-native, fully automated, minimal maintenance
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessMetricsPage;
